import { useState } from 'react'
import { X } from 'lucide-react'
import { inviteEntity } from '../../services/api'
import type { AggregatorProject } from '../../types'
import { useOrgType } from '../../contexts/OrgTypeContext'
import { INVITE_PLAN_FIELD } from '../../lib/portfolioMembership'

interface BulkInviteDialogProps {
  selectedProjectIds: string[]
  projects: AggregatorProject[]
  activeAggregatorId: string | null
  onClose: () => void
}

interface EntityEntry {
  entityId: string
  entityName: string
  email: string
}

type SendStatus = 'pending' | 'sending' | 'success' | 'conflict' | 'error'

interface SendResult {
  entityId: string
  status: SendStatus
  errorMessage?: string
}

export default function BulkInviteDialog({
  selectedProjectIds,
  projects,
  activeAggregatorId,
  onClose,
}: BulkInviteDialogProps) {
  // De-duplicate by entity_id
  const entityMap = new Map<string, EntityEntry>()
  for (const pid of selectedProjectIds) {
    const proj = projects.find((p) => p.project_id === pid)
    if (proj && !entityMap.has(proj.entity_id)) {
      entityMap.set(proj.entity_id, {
        entityId: proj.entity_id,
        entityName: proj.entity_name ?? proj.entity_id,
        email: '',
      })
    }
  }
  const initialEntities = Array.from(entityMap.values())

  const { vocab } = useOrgType()
  const [entities, setEntities] = useState<EntityEntry[]>(initialEntities)
  const [sending, setSending] = useState(false)
  const [results, setResults] = useState<SendResult[] | null>(null)

  const updateEmail = (entityId: string, email: string) => {
    setEntities((prev) => prev.map((e) => (e.entityId === entityId ? { ...e, email } : e)))
  }

  // BUG-129: the old Send button only checked `entities.length === 0`, so it was
  // enabled with blank/invalid rows — every empty row guaranteed a 400 from the
  // invite API. Gate Send on every row carrying a syntactically valid email.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const allEmailsValid = entities.length > 0 && entities.every((e) => EMAIL_RE.test(e.email.trim()))

  const successCount = results?.filter((r) => r.status === 'success').length ?? 0

  const handleSend = async () => {
    setSending(true)
    const sendResults: SendResult[] = []

    for (let i = 0; i < entities.length; i++) {
      const entry = entities[i]
      setResults([
        ...sendResults,
        { entityId: entry.entityId, status: 'sending' },
        ...entities.slice(i + 1).map((e) => ({ entityId: e.entityId, status: 'pending' as SendStatus })),
      ])
      try {
        await inviteEntity(entry.entityId, entry.email, INVITE_PLAN_FIELD, activeAggregatorId ?? '')
        sendResults.push({ entityId: entry.entityId, status: 'success' })
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err)
        if (message.includes('409') || message.includes('Conflict')) {
          sendResults.push({ entityId: entry.entityId, status: 'conflict' })
        } else {
          sendResults.push({ entityId: entry.entityId, status: 'error', errorMessage: message })
        }
      }
    }

    setResults(sendResults)
    setSending(false)
  }

  const isDone = results !== null && !sending

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{vocab.inviteDialogTitle}</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {!results ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Sending invitations to {entities.length} {entities.length === 1 ? vocab.entitySingular : vocab.entityPlural}.
            </p>

            <div className="space-y-3 mb-4">
              {entities.map((entry) => (
                <div key={entry.entityId} className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-800 w-36 flex-shrink-0 truncate" title={entry.entityName}>
                    {entry.entityName}
                  </span>
                  <input
                    type="email"
                    data-testid={`invite-email-${entry.entityId}`}
                    value={entry.email}
                    onChange={(e) => updateEmail(entry.entityId, e.target.value)}
                    placeholder="email@example.com"
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              ))}
            </div>

            {!allEmailsValid && (
              <p className="text-xs text-amber-600 mb-4" data-testid="invite-email-hint">
                Enter a valid email address for every {vocab.entitySingular} before sending.
              </p>
            )}


            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                data-testid="invite-send"
                onClick={handleSend}
                disabled={sending || entities.length === 0 || !allEmailsValid}
                className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
              >
                Send invites
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2 mb-5" data-testid="invite-progress">
              {results.map((r, idx) => {
                let statusText = ''
                if (r.status === 'sending') statusText = '…'
                else if (r.status === 'success') statusText = '✓'
                else if (r.status === 'conflict') statusText = `✗ (${vocab.conflictLabel})`
                else if (r.status === 'error') statusText = `✗ (failed — ${r.errorMessage ?? 'unknown error'})`
                else statusText = '…'

                return (
                  <div key={r.entityId} className="text-sm text-gray-700" data-testid={`invite-result-${r.entityId}`}>
                    Sending {idx + 1} of {entities.length}... {statusText}
                  </div>
                )
              })}
            </div>

            {isDone && (
              <>
                <p className="text-sm font-semibold text-gray-800 mb-4" data-testid="invite-summary">
                  {successCount} of {entities.length} invites sent successfully
                </p>
                <button
                  data-testid="invite-done"
                  onClick={onClose}
                  className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700"
                >
                  Done
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
